import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tabs,
	Tab,
	Typography,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { isValidDateTest, maxLengthMessage } from "../../utils/functions";
import PATAddButton from "../../components/Buttons/PATAddButton";
import TextFieldController from "../../components/inputs/TextFieldController";

const vehicleSchema = yup.object().shape({
	// Vehicle Info
	unitNumber: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	inventoryNumber: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.nullable(),
	status: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	vin: yup.string().trim().max(17, maxLengthMessage(17)).nullable(),
	make: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	model: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	year: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	division: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	assignedOfficerID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),

	// Mileage
	odometer: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	lastMileage: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	startingMileage: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),

	// Acquisition
	purchaseDate: isValidDateTest("Purchase Date"),
	originalCost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	acquiredFrom: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.nullable(),
	fundingSourceID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),

	// Registration
	licenseNumber: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	licenseExpiration: yup.string().nullable(),
});

const TabPanel = ({ children, value, index }) =>
	value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const buildDefaults = (initialData) => {
	return {
		unitNumber: initialData?.unitNumber || "",
		inventoryNumber: initialData?.inventoryNumber || "",
		status: initialData?.status || "",
		vin: initialData?.vin || "",
		make: initialData?.make || "",
		model: initialData?.model || "",
		year: initialData?.year || "",
		division: initialData?.division || "",
		assignedOfficerID: initialData?.assignedOfficerID
			? String(initialData.assignedOfficerID)
			: "",
		notes: initialData?.notes || "",

		odometer: initialData?.odometer || "",
		lastMileage: initialData?.lastMileage || "",
		startingMileage: initialData?.startingMileage || "",

		purchaseDate: initialData?.purchaseDate
			? moment(initialData.purchaseDate).format("MM/DD/YYYY")
			: "",
		originalCost: initialData?.originalCost || "",
		acquiredFrom: initialData?.acquiredFrom || "",
		fundingSourceID: initialData?.fundingSourceID
			? String(initialData.fundingSourceID)
			: "",

		licenseNumber: initialData?.licenseNumber || "",
		licenseExpiration: initialData?.licenseExpiration || "",

		serviceRequirements: initialData?.serviceRequirements || [],
	};
};

const AddEditVehicleDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
	// Option arrays — pass the same shape you use elsewhere: [{ value, label }]
	statusOptions,
	divisionOptions,
	employeeOptions,
	fundingSourceOptions,
	serviceRequirements, // full list from codes
}) => {
	const user = useSelector((state) => state.user);
	const [tab, setTab] = React.useState(0);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		getValues,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(vehicleSchema),
		defaultValues: buildDefaults(initialData),
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData));
		setTab(0);
	}, [initialData, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
			setTab(0);
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			unitNumber: data.unitNumber?.trim() || null,
			inventoryNumber: data.inventoryNumber?.trim() || null,
			status: data.status || null,
			vin: data.vin?.trim() || null,
			make: data.make?.trim() || null,
			model: data.model?.trim() || null,
			year: data.year || null,
			division: data.division || null,
			assignedOfficerID: data.assignedOfficerID || null,
			notes: data.notes?.trim() || null,

			odometer: data.odometer || null,
			lastMileage: data.lastMileage || null,
			startingMileage: data.startingMileage || null,

			purchaseDate: data.purchaseDate
				? moment(data.purchaseDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			originalCost: data.originalCost || null,
			acquiredFrom: data.acquiredFrom?.trim() || null,
			fundingSourceID: data.fundingSourceID || null,

			licenseNumber: data.licenseNumber?.trim() || null,
			licenseExpiration: data.licenseExpiration || null,

			serviceRequirements: data.serviceRequirements || [],

			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	// ── Service requirements helpers (managed inside watch) ──────────────────
	const assignedReqs = watch("serviceRequirements") || [];

	const addReq = (id) =>
		setValue("serviceRequirements", [...assignedReqs, id], {
			shouldDirty: true,
		});

	const removeReq = (id) =>
		setValue(
			"serviceRequirements",
			assignedReqs.filter((x) => x !== id),
			{ shouldDirty: true },
		);

	const activeReqs = (serviceRequirements || []).filter(
		(r) => r.isActive !== false,
	);
	const availableReqs = activeReqs.filter(
		(r) => !assignedReqs.includes(r.id),
	);

	// Mileage computed display
	const odometer = watch("odometer") || 0;
	const startingMileage = watch("startingMileage") || 0;
	const totalMiles = odometer - startingMileage;

	// License expiry check
	const licenseExpiration = watch("licenseExpiration");
	const licenseExpired =
		licenseExpiration && new Date(licenseExpiration + "-01") < new Date();

	const dialogTitle = initialData
		? viewOnly
			? `View Vehicle — ${initialData.unitNumber}`
			: `Edit Vehicle — ${initialData.unitNumber}`
		: "Add New Vehicle";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab label="Vehicle Info" />
					<Tab label="Mileage" />
					<Tab label="Acquisition" />
					<Tab label="Registration" />
					<Tab label="Service Req." />
				</Tabs>
			</Box>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					{/* ── Vehicle Info ── */}
					<TabPanel value={tab} index={0}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(3, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<TextFieldController
									name="unitNumber"
									label="Unit Number"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Box>
							<Box>
								<TextFieldController
									name="inventoryNumber"
									label="Inventory Number"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<DropdownController
									name="status"
									label="Status"
									options={statusOptions || []}
									control={control}
									errors={errors}
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<TextFieldController
									name="vin"
									label="VIN"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="make"
									label="Make"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="model"
									label="Model"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<DropdownController
									name="year"
									label="Year"
									options={[]}
									control={control}
									errors={errors}
									freeSolo
									type="number"
								/>
							</Box>
							<Box>
								<DropdownController
									name="division"
									label="Division"
									options={divisionOptions || []}
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<DropdownController
									name="assignedOfficerID"
									label="Assigned Employee"
									options={employeeOptions || []}
									control={control}
									errors={errors}
									placeholder="— Unassigned —"
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<TextFieldController
									name="notes"
									label="Notes"
									control={control}
									errors={errors}
									multiline
									rows={2}
								/>
							</Box>
						</Box>
					</TabPanel>

					{/* ── Mileage ── */}
					<TabPanel value={tab} index={1}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(3, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<TextFieldController
									name="odometer"
									label="Current Odometer (mi)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box>
								<TextFieldController
									name="lastMileage"
									label="Last Mileage (mi)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box>
								<TextFieldController
									name="startingMileage"
									label="Starting Mileage (mi)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>

							{odometer || startingMileage ? (
								<Box
									sx={{
										gridColumn: "1 / -1",
										p: 2,
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										display: "flex",
										alignItems: "center",
										gap: 2,
									}}
								>
									<Typography
										variant="caption"
										fontWeight={600}
										color="text.secondary"
									>
										Total Miles
									</Typography>
									<Typography
										variant="body1"
										fontWeight={700}
										color={
											totalMiles > 150000
												? "error.main"
												: "success.main"
										}
										fontFamily="monospace"
									>
										{totalMiles.toLocaleString()} mi
									</Typography>
									{totalMiles > 150000 && (
										<Typography
											variant="caption"
											fontWeight={700}
											color="error.main"
										>
											⚠ High mileage
										</Typography>
									)}
								</Box>
							) : null}
						</Box>
					</TabPanel>

					{/* ── Acquisition ── */}
					<TabPanel value={tab} index={2}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<DatePickerController
									name="purchaseDate"
									label="Purchase Date"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="originalCost"
									label="Original Cost ($)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<TextFieldController
									name="acquiredFrom"
									label="Purchased / Acquired From"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<DropdownController
									name="fundingSourceID"
									label="Funding Source"
									options={fundingSourceOptions || []}
									control={control}
									errors={errors}
									placeholder="— None —"
								/>
							</Box>
						</Box>
					</TabPanel>

					{/* ── Registration ── */}
					<TabPanel value={tab} index={3}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<TextFieldController
									name="licenseNumber"
									label="License Number"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="licenseExpiration"
									label="License Expiration (YYYY-MM)"
									control={control}
									errors={errors}
								/>
								{licenseExpired && (
									<Typography
										variant="caption"
										color="error"
										fontWeight={700}
										sx={{ mt: 0.5, display: "block" }}
									>
										⚠ License is expired
									</Typography>
								)}
							</Box>
						</Box>
					</TabPanel>

					{/* ── Service Requirements ── */}
					<TabPanel value={tab} index={4}>
						{/* Assigned */}
						{assignedReqs.length === 0 ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ textAlign: "center", py: 3 }}
							>
								No service requirements assigned. Use the list
								below to add.
							</Typography>
						) : (
							<Box sx={{ mb: 2 }}>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Assigned Requirements
								</Typography>
								{assignedReqs.map((id) => {
									const req = activeReqs.find(
										(r) => r.id === id,
									);
									if (!req) return null;
									return (
										<Box
											key={id}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1.5,
												p: "10px 14px",
												border: "1px solid",
												borderColor: "divider",
												borderRadius: 1,
												mb: 1,
											}}
										>
											<Box sx={{ flex: 1 }}>
												<Typography
													variant="body2"
													fontWeight={600}
												>
													{req.label}
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													fontFamily="monospace"
												>
													Every{" "}
													{Number(
														req.numericValue,
													).toLocaleString()}{" "}
													{req.unit}
												</Typography>
											</Box>
											<PATCloseButton
												onClick={() => removeReq(id)}
												tooltipText="Remove"
												size="small"
											/>
										</Box>
									);
								})}
							</Box>
						)}

						{/* Available to add */}
						{availableReqs.length > 0 && (
							<Box>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Add Requirement
								</Typography>
								{availableReqs.map((req) => (
									<Box
										key={req.id}
										onClick={() => addReq(req.id)}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1.5,
											p: "8px 14px",
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 1,
											mb: 0.75,
											cursor: "pointer",
											"&:hover": {
												borderColor: "primary.main",
											},
										}}
									>
										<Box sx={{ flex: 1 }}>
											<Typography
												variant="body2"
												fontWeight={600}
											>
												{req.label}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												fontFamily="monospace"
											>
												Every{" "}
												{Number(
													req.numericValue,
												).toLocaleString()}{" "}
												{req.unit}
											</Typography>
										</Box>
										<PATAddButton
											onClick={(e) => {
												e.stopPropagation();
												addReq(req.id);
											}}
											tooltipText="Add"
											size="small"
										/>
									</Box>
								))}
							</Box>
						)}

						{availableReqs.length === 0 &&
							assignedReqs.length > 0 && (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										textAlign: "center",
										display: "block",
										pt: 1,
									}}
								>
									All available service requirements are
									assigned.
								</Typography>
							)}
					</TabPanel>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save"
					/>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Close" />
			</DialogActions>
		</Dialog>
	);
};

export default AddEditVehicleDialog;
